import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { VaccinationRecommendation } from '../../types/api';

const VaccinationRecommendationsPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [recommendations, setRecommendations] = useState<VaccinationRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadRecommendations();
    }
  }, [selectedLocation?.id, businessType]);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(
        `/goat/vaccination-recommendations?location_id=${selectedLocation?.id}`
      );
      setRecommendations(response.data || []);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load vaccination recommendations'));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRecommendation = (recommendation: VaccinationRecommendation) => {
    const goatTags = recommendation.goats.map(g => g.tag).join(',');
    return `/goat/health-logs/vaccination/new?vaccine_type=${recommendation.vaccine_type}&goats=${goatTags}`;
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  const getVaccineTypeColor = (vaccineType: string) => {
    switch (vaccineType) {
      case 'PPR': return 'bg-blue-100 text-blue-800';
      case 'ET_TT': return 'bg-green-100 text-green-800';
      case 'FMD_HS_BQ': return 'bg-purple-100 text-purple-800';
      case 'GOAT_POX': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReasonText = (reason: string) => {
    switch (reason) {
      case 'never_vaccinated':
        return 'Never vaccinated';
      case 'due_for_booster':
        return 'Due for booster';
      case 'new_goat_plan':
        return 'New Goat Plan';
      default:
        return reason;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'too_young':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/health-logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">💉 Vaccination Recommendations</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* New Goat Plan Info */}
      {recommendations.some(r => r.is_new_goat_plan) && (
        <div className="card p-4 bg-blue-50 border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">📋 New Goat Vaccination Plan</h3>
          <p className="text-sm text-blue-800 mb-2">
            Goats that have never been vaccinated follow a special initial schedule:
          </p>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li><strong>Day 3-5:</strong> PPR Vaccine (Priority 1)</li>
            <li><strong>Day 10-12:</strong> ET + TT Vaccine (Booster after 21 days, first time only)</li>
            <li><strong>Day 14-16:</strong> Goat Pox Vaccine</li>
            <li><strong>Day 21-28:</strong> Triovac (FMD + HS + BQ)</li>
          </ul>
          <p className="text-xs text-blue-700 mt-2">
            ⚠️ Maintain 7-10 days gap between vaccines. Do NOT give multiple vaccines on the same day.
          </p>
        </div>
      )}

      {/* Recommendations */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : recommendations.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="text-4xl mb-4">✅</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">All Up to Date!</h2>
          <p className="text-gray-600 mb-6">
            All goats are up to date with their vaccinations.
          </p>
          <Link
            to="/goat/health-logs"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Health Logs
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {recommendations.map((recommendation) => (
            <div key={recommendation.vaccine_type} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {recommendation.vaccine_name}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getVaccineTypeColor(recommendation.vaccine_type)}`}>
                      {recommendation.vaccine_type}
                    </span>
                    {recommendation.is_new_goat_plan && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                        New Goat Plan
                      </span>
                    )}
                  </div>
                  {recommendation.is_new_goat_plan && recommendation.schedule_info && (
                    <div className="mb-2 p-2 bg-blue-50 rounded text-sm">
                      <p className="font-medium text-blue-900">
                        Schedule: Day {recommendation.schedule_info.day_start}-{recommendation.schedule_info.day_end} after arrival
                      </p>
                      {recommendation.schedule_info.booster_after_days && (
                        <p className="text-blue-700 text-xs mt-1">
                          Booster required after {recommendation.schedule_info.booster_after_days} days (first time only)
                        </p>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-gray-600">
                    {recommendation.goats.length} goat{recommendation.goats.length !== 1 ? 's' : ''} need{recommendation.goats.length === 1 ? 's' : ''} vaccination
                  </p>
                </div>
                <Link
                  to={handleAcceptRecommendation(recommendation)}
                  className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2 ml-4"
                >
                  <span>Accept & Vaccinate</span>
                </Link>
              </div>

              <div className="border-t pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {recommendation.goats.map((goat, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">Tag: {goat.tag}</span>
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            goat.reason === 'never_vaccinated'
                              ? 'bg-yellow-100 text-yellow-800'
                              : goat.reason === 'new_goat_plan'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {getReasonText(goat.reason)}
                          </span>
                          {goat.status && (
                            <span className={`px-2 py-0.5 text-xs rounded ${getStatusColor(goat.status)}`}>
                              {goat.status}
                            </span>
                          )}
                        </div>
                        {goat.days_since_arrival !== undefined && (
                          <p className="text-xs text-gray-600 mt-1">
                            {goat.days_since_arrival} days since arrival
                            {goat.recommended_day && ` • ${goat.recommended_day}`}
                            {goat.age_months !== undefined && ` • Age: ${goat.age_months.toFixed(1)} months`}
                          </p>
                        )}
                        {goat.purchase_date && (
                          <p className="text-xs text-gray-500 mt-1">
                            Arrived: {new Date(goat.purchase_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {goat.last_vaccination_date && (
                        <span className="text-xs text-gray-500 ml-2">
                          Last: {new Date(goat.last_vaccination_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VaccinationRecommendationsPage;
